import { interval, Subscription } from 'rxjs'
import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { ManageQueues as ManageQueuesLanguage, UsersByExtension as UsersByExtensionLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DEVICE_FILTER_ACD_GROUPS, MANAGE_QUEUES_FIELDS } from '../../../NonReact/constants/DeviceConstants'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { handleUnknownForAvailables } from '../../AddUser/AddUserFunctions'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import {
    ALL_VALUE,
    getDevicesFromPartitionByCache,
    getPartitionsForTelServer,
    getTelephonyServer,
    getTelServersAsSelectOptionsWithFilters
} from '../../Common/API/ItemsGetters'
import { PaginatedList } from '../../Common/Components/PaginatedList/PaginatedList'
import { DeviceSelectItem, SelectOption, SelectOptions } from '../../Common/Components/Selects/Types'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { useStateAsync } from '../../Common/Hooks/useStateAsync'
import { IndividualTelItem, ReactState, ReactStateAsync } from '../../Common/Types/Types'
import { ManageQueueEntry } from '../Entries/ManageQueueEntry'
import { FilterTypes, UpdateQueueRequestBody } from '../Types/Types'
import { MonitoringStatuses } from '../Constants/Constants'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { deviceURL, NO_CACHE_TIME } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { MonitoringButton } from '../Header/MonitoringButton'
import { IconName, IconSize } from '../../../NonReact/constants/React/Icon/Constants'
import { EMPTY_STRING, SPACE } from '../../Common/Constants/Separators'
import { TelSysTypes } from '../../TelSysConfiguration/Constants/Constants'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import {
    AUTH_USER_MISSING_ROLES,
    BOTS_DISABLED,
    COMMS_ENABLED,
    HISTORIC_ONLY,
    MANUAL_RT_QUEUE_ENABLED
} from '../../../NonReact/constants/TelSysConstants'
import { COMMON_DEVICES_PARTITION_NAME } from '../../../NonReact/constants/Common'
import { ComboBoxAlt } from '../../Common/Components/ComboBox/ComboBoxAlt'
import { StatusMessage } from '../../Common/Components/StatusMessage/StatusMessage'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'

export const ManageQueues: FC = (): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(ManageQueuesLanguage.NAME)

    const [userInfo]: UserInfo = useUserInfo()
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [telSys, setTelSys]: ReactState<number> = useState<number>(ALL_VALUE)
    const [partition, setPartition]: ReactState<number> = useState<number>(0)
    const [filterOption, setFilterOption]: ReactState<FilterTypes> = useState<FilterTypes>(FilterTypes.ALL)
    const [availableQueues, setAvailableQueues]: ReactState<DeviceSelectItem[]> = useState<DeviceSelectItem[]>([])
    const [queueErrors, setQueueErrors, getQueueErrors]: ReactStateAsync<Definable<string>[]> = useStateAsync<
        Definable<string>[]
    >([])
    const [queuesPending, setQueuesPending]: ReactState<DeviceSelectItem[]> = useState<DeviceSelectItem[]>([])

    const [availableTelServers, setAvailableTelServers]: ReactState<Definable<SelectOptions<number>>> =
        useState<SelectOptions<number>>()
    const [availablePartitions, setAvailablePartitions]: ReactState<Definable<SelectOptions<number>>> =
        useState<SelectOptions<number>>()

    const [currentTelSys, setCurrentTelSys]: ReactState<Nullable<IndividualTelItem>> =
        useState<Nullable<IndividualTelItem>>()

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)

    const [loadingText, setLoadingText]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const intervalSub: MutableRefObject<Nullable<Subscription>> = useRef<Nullable<Subscription>>()

    const changeQueueRealTimeMode = async (disableRealTime: boolean, deviceNumber: string): Promise<boolean> =>
        requestJSON(deviceURL(`${telSys}`, `${partition}`, `${deviceNumber}`), {
            method: RESTMethods.PUT,
            body: {
                DisableRTMonitoring: disableRealTime
            } as UpdateQueueRequestBody
        })
            .then((): boolean => true)
            .catch((e: Error): boolean => {
                displayError(e)
                return false
            })

    const fetchTelephonyServersAndPartitions = useCallback((newTelSysValue: number): void => {
        getTelephonyServer(
            newTelSysValue,
            HISTORIC_ONLY,
            COMMS_ENABLED,
            BOTS_DISABLED,
            AUTH_USER_MISSING_ROLES,
            MANUAL_RT_QUEUE_ENABLED
        )
            .then(setCurrentTelSys)
            .catch(displayError)

        getPartitionsForTelServer(newTelSysValue)
            .then(
                (partitions: SelectOptions<number>): SelectOptions<number> =>
                    partitions.filter(
                        (partitionFilter: SelectOption<number>): boolean =>
                            partitionFilter.label !== COMMON_DEVICES_PARTITION_NAME
                    )
            )
            .then((partitions: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setIsLoadingPartitions(false)

                    if (partitions.length > 0) {
                        const newPartitionValue: number = partitions.find((): boolean => true)?.value ?? 0

                        setPartition(newPartitionValue ?? 0)

                        handleUnknownForAvailables(partitions, setAvailablePartitions, partition)

                        fetchDevicesFromPartition(newTelSysValue, newPartitionValue)
                    }
                }
            })
            .finally((): void => setIsLoadingPartitions(false))
            .catch(displayError)
    }, [])

    const filterQueuesOptions: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            { label: language.get(ManageQueuesLanguage.ManageQueuesQueueFilterAll), value: FilterTypes.ALL },
            { label: language.get(ManageQueuesLanguage.ManageQueuesFilterCompatible), value: FilterTypes.COMPATIBLE },
            {
                label: language.get(ManageQueuesLanguage.ManageQueuesFilterIncompatible),
                value: FilterTypes.INCOMPATIBLE
            }
        ],
        []
    )

    const filteredQueues: DeviceSelectItem[] = useMemo(
        (): DeviceSelectItem[] =>
            availableQueues.filter((queue: DeviceSelectItem): boolean => {
                if (filterOption === FilterTypes.ALL) {
                    return true
                }

                switch (filterOption) {
                    case FilterTypes.COMPATIBLE:
                        return queue.original?.QueueCompatibility === true
                    case FilterTypes.INCOMPATIBLE:
                        return queue.original?.QueueCompatibility === false
                    default:
                        return true
                }
            }),
        [availableQueues, filterOption]
    )

    const isMonitoredAllButtonEnabled: boolean = useMemo(
        (): boolean =>
            (currentTelSys?.CommsEnabled ?? false) &&
            !currentTelSys?.BotsDisabled &&
            !currentTelSys?.HistoricOnly &&
            filteredQueues.filter(
                (queue: DeviceSelectItem): boolean => queue.original?.QueueStatus === MonitoringStatuses.HISTORIC
            ).length > 0,
        [JSON.stringify(queuesPending), JSON.stringify(filteredQueues), currentTelSys]
    )

    const isUnmonitoredAllButtonEnabled: boolean = useMemo(
        (): boolean =>
            (currentTelSys?.CommsEnabled ?? false) &&
            !currentTelSys?.BotsDisabled &&
            !currentTelSys?.HistoricOnly &&
            filteredQueues.filter(
                (queue: DeviceSelectItem): boolean =>
                    queue.original?.QueueStatus === MonitoringStatuses.REAL_TIME_FORCED ||
                    queue.original?.QueueStatus === MonitoringStatuses.REAL_TIME
            ).length > 0,
        [JSON.stringify(queuesPending), JSON.stringify(filteredQueues), currentTelSys]
    )

    const isManualRTEnabled: boolean = currentTelSys?.ManualRTQueuesEnabled ?? false

    const fetchDevicesFromPartition = useCallback(
        (telsysValue: number, partitionValue: number, setLoading?: (loading: boolean) => void): void => {
            if (telsysValue && partitionValue) {
                setLoadingText(language.get(UsersByExtensionLanguage.AddAllUsers_Loading))

                getDevicesFromPartitionByCache(
                    telsysValue,
                    partitionValue,
                    false,
                    DEVICE_FILTER_ACD_GROUPS,
                    undefined,
                    undefined,
                    undefined,
                    NO_CACHE_TIME,
                    MANAGE_QUEUES_FIELDS
                )
                    .then(([devices]: [DeviceSelectItem[], Definable<number>]): void => {
                        const processedDevices: DeviceSelectItem[] = devices
                            .reduce((acc: DeviceSelectItem[], device: DeviceSelectItem): DeviceSelectItem[] => {
                                const existingDevice: Definable<DeviceSelectItem> = acc.find(
                                    (d: DeviceSelectItem): boolean =>
                                        d.original?.IDOnTelSysOwner === device.original?.IDOnTelSysOwner
                                )

                                if (existingDevice) {
                                    existingDevice.additionalDevices = existingDevice.additionalDevices ?? []
                                    existingDevice.additionalDevices.push(device)

                                    return acc
                                }

                                return [...acc, device]
                            }, [])
                            .map(
                                (device: DeviceSelectItem, index: number): DeviceSelectItem => ({
                                    ...device,
                                    index
                                })
                            )

                        setAvailableQueues(processedDevices)
                        setQueueErrors(
                            processedDevices.map((device: DeviceSelectItem): Definable<string> => {
                                if (
                                    isVarNotEmpty(device.original?.CallQueueCaptureErrorMessage) &&
                                    (device.original?.QueueStatus !== MonitoringStatuses.DISABLING_MONITORING ||
                                        device.original?.QueueStatus !== MonitoringStatuses.ENABLING_MONITORING)
                                ) {
                                    return device.original?.CallQueueCaptureErrorMessage
                                }
                                return undefined
                            })
                        )
                        setLoadingText(undefined)

                        if (isMounted.current) {
                            // check if queues are "DisablingMonitoring" or "EnablingMonitoring"
                            setQueuesPending(
                                devices.filter(
                                    (device: DeviceSelectItem): boolean =>
                                        device.original?.QueueStatus === MonitoringStatuses.DISABLING_MONITORING ||
                                        device.original?.QueueStatus === MonitoringStatuses.ENABLING_MONITORING ||
                                        device.original?.QueueStatus === null
                                )
                            )
                        }
                    })
                    .catch((e: Error): void => {
                        displayError(e)
                        setLoadingText(undefined)
                    })
                    .finally((): void => {
                        if (isVarNotEmpty(setLoading)) {
                            setLoading(false)
                        }
                    })
            }
        },
        [displayError, language, setLoadingText]
    )

    const updateRealTimeModeQueues = useCallback(
        (disableRealTime: boolean): void => {
            setLoadingText(
                language.get(
                    disableRealTime
                        ? ManageQueuesLanguage.ManageQueue_Loading_Monitoring_All
                        : ManageQueuesLanguage.ManageQueue_Loading_Unmonitoring_All
                )
            )

            const queues: Promise<boolean>[] = filteredQueues
                .filter((queue: DeviceSelectItem): boolean =>
                    disableRealTime
                        ? queue?.original?.QueueStatus === MonitoringStatuses.REAL_TIME_FORCED ||
                          queue?.original?.QueueStatus === MonitoringStatuses.REAL_TIME
                        : queue?.original?.QueueStatus === MonitoringStatuses.HISTORIC
                )
                .map((queue: DeviceSelectItem) =>
                    changeQueueRealTimeMode(disableRealTime, queue?.original?.DeviceNumber ?? EMPTY_STRING)
                )

            Promise.all(queues)
                .then((): void => {
                    if (isMounted.current) {
                        fetchDevicesFromPartition(telSys, partition)
                    }
                })
                .catch(displayError)
                .finally((): void => setLoadingText(undefined))
        },
        [filteredQueues]
    )

    const handleTelsysChange = useCallback((newTelSysValue: Nullable<number | number[]>): void => {
        if (IsVarEmpty(newTelSysValue) || Array.isArray(newTelSysValue)) return

        setTelSys(newTelSysValue)
        setIsLoadingPartitions(true)

        setAvailableQueues([])
        setQueuesPending([])

        fetchTelephonyServersAndPartitions(newTelSysValue)
    }, [])

    const handlePartitionChange = (newPartitionValue: Nullable<number | number[]>): void => {
        if (IsVarEmpty(newPartitionValue) || Array.isArray(newPartitionValue)) return

        setAvailableQueues([])
        setQueuesPending([])

        setPartition(newPartitionValue)

        fetchDevicesFromPartition(telSys, newPartitionValue)
    }

    const handleFilterOptionChange = useCallback((newFilterOption: Nullable<string | string[]>): void => {
        if (IsVarEmpty(newFilterOption) || Array.isArray(newFilterOption)) return

        setFilterOption(newFilterOption as FilterTypes)
    }, [])

    useEffect((): void => {
        if (userInfo) {
            setIsLoadingTelServers(true)
            setIsLoadingPartitions(true)

            getTelServersAsSelectOptionsWithFilters(
                userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID,
                [TelSysTypes.MS_TEAMS],
                true,
                ReportingAccessLevels.ENTERPRISE
            )
                .then((telServers: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingTelServers(false)
                        setAvailablePartitions(undefined)

                        let newTelSysValue: number

                        if (
                            userInfo?.Scope?.TelSysID &&
                            userInfo.Scope.TelSysID.length > 0 &&
                            userInfo.Scope.TelSysID[0] === ALL_VALUE &&
                            telServers.length > 0
                        ) {
                            newTelSysValue = telServers[0].value
                        } else {
                            newTelSysValue = userInfo.Scope.TelSysID[0]
                        }

                        setTelSys(newTelSysValue)

                        handleUnknownForAvailables(telServers, setAvailableTelServers, newTelSysValue)

                        if (newTelSysValue === ALL_VALUE) return

                        fetchTelephonyServersAndPartitions(newTelSysValue)
                    }
                })
                .catch(displayError)
        }
    }, [userInfo])

    useEffect((): Definable<() => void> => {
        intervalSub.current?.unsubscribe()

        if (queuesPending.length <= 0) return undefined

        intervalSub.current = interval(5000).subscribe((): void => fetchDevicesFromPartition(telSys, partition))

        return (): void => intervalSub.current?.unsubscribe()
    }, [JSON.stringify(queuesPending), telSys, partition])

    return (
        <>
            <div className='manage-queues'>
                <PaginatedList
                    entries={filteredQueues}
                    overrideLanguages={{
                        NotFound: language.get(ManageQueuesLanguage.ManageQueues_Not_Found_Error)
                    }}
                    entriesPerPage={10}
                    errors={queueErrors}
                    isLoading={isStringNotEmpty(loadingText)}
                    loadingTitle={loadingText}
                    objectSearchMutator={({ label, value }: DeviceSelectItem): string => [label, value].join(SPACE)}
                    entryDisplayAsyncMapper={(extension: DeviceSelectItem, index: number): Promise<ReactElement> =>
                        getQueueErrors().then(
                            (errors: Definable<string>[]): ReactElement => (
                                <ManageQueueEntry
                                    key={`${JSON.stringify(extension)}-${index}`}
                                    callQueue={extension}
                                    queueError={errors[index]}
                                    currentTelSys={currentTelSys}
                                    telsys={telSys}
                                    partition={partition}
                                    fetchDevicesFromPartition={fetchDevicesFromPartition}
                                    isManualRTEnabled={isManualRTEnabled}
                                />
                            )
                        )
                    }
                    entryHeaders={[
                        { header: language.get(ManageQueuesLanguage.ManageQueuesDescriptionTitle) },
                        ...(!isManualRTEnabled
                            ? [{ header: language.get(ManageQueuesLanguage.ManageQueuesCQConfigurationsTitle) }]
                            : []),
                        { header: language.get(ManageQueuesLanguage.ManageQueuesResourceAccountsTitle) },
                        { header: language.get(ManageQueuesLanguage.ManageQueuesMonitoringStatusTitle) }
                    ]}
                    subHeaderContent={
                        <div className='mq-subheader-content-container'>
                            {!ArrayUtils.isEmpty(currentTelSys?.AuthUserMissingRoles) ? (
                                <StatusMessage
                                    message={language.get(ManageQueuesLanguage.ManageQueues_ExchangePermission_Error)}
                                    type='error'
                                />
                            ) : (
                                <></>
                            )}
                            <div className='mq-subheader-content-right'>
                                <div className='compatible-filter'>
                                    <ComboBoxAlt
                                        label='Show'
                                        value={filterOption}
                                        options={filterQueuesOptions}
                                        handleChange={handleFilterOptionChange}
                                    />
                                </div>
                                <MonitoringButton
                                    guardMessage={language.get(ManageQueuesLanguage.ManageQueue_MonitorAllMessage)}
                                    popperMessage={language.get(ManageQueuesLanguage.ManageQueue_Enable_RT_Popper)}
                                    disabled={isMonitoredAllButtonEnabled}
                                    icon={IconName.QUEUE_MONITOR_ALL}
                                    onClick={(): void => updateRealTimeModeQueues(false)}
                                />
                                <MonitoringButton
                                    guardMessage={language.get(ManageQueuesLanguage.ManageQueue_UnmonitorAllMessage)}
                                    popperMessage={language.get(ManageQueuesLanguage.ManageQueue_Disable_RT_Popper)}
                                    disabled={isUnmonitoredAllButtonEnabled}
                                    icon={IconName.QUEUE_UNMONITOR_ALL}
                                    onClick={(): void => updateRealTimeModeQueues(true)}
                                />
                                <HelpTag
                                    iconSize={IconSize.SMALL}
                                    tag={Tags.MANAGE_QUEUE}
                                    helpContentTitle={HelpTagTitles.MANAGE_QUEUE}
                                />
                            </div>
                        </div>
                    }
                    headerContent={
                        availableTelServers && availableTelServers.length > 0 ? (
                            <div className='mq-filters'>
                                <div className='mq-filter-title'>
                                    {language.get(ManageQueuesLanguage.ManageQueueFiltersTitle)}
                                </div>
                                <div className='mq-filter-options'>
                                    <ComboBoxAlt
                                        label=''
                                        value={telSys}
                                        options={availableTelServers}
                                        disableClearing
                                        disabled={
                                            isLoadingTelServers ||
                                            (userInfo?.Scope?.TelSysID?.length === 1 &&
                                                userInfo?.Scope?.TelSysID[0] !== ALL_VALUE)
                                        }
                                        handleChange={handleTelsysChange}
                                    />
                                    {availablePartitions && availablePartitions.length > 0 ? (
                                        <ComboBoxAlt
                                            label=''
                                            value={partition}
                                            options={availablePartitions}
                                            disableClearing
                                            disabled={
                                                isLoadingPartitions ||
                                                (userInfo?.Scope?.PartIDInTelSys?.length === 1 &&
                                                    userInfo?.Scope?.PartIDInTelSys[0] !== ALL_VALUE)
                                            }
                                            handleChange={handlePartitionChange}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </div>
                        ) : undefined
                    }
                />
            </div>
        </>
    )
}
